import { ICON_PATTERNS } from './constants';
import {
  SUPPORTED_ICON_NAMES,
  type TechnologyIconName,
} from './supported-icon-names';

const SUPPORTED_ICON_BY_NORMALIZED_NAME = new Map<string, TechnologyIconName>(
  SUPPORTED_ICON_NAMES.map((iconName) => [normalizeKey(iconName), iconName]),
);

const GENERIC_NOISE_TOKENS = new Set([
  'bot',
  'management',
  'manager',
  'premium',
  'server',
  'framework',
  'library',
  'runtime',
  'plugin',
  'module',
  'fonts',
  'font',
  'cms',
  'cdn',
  'seo',
]);

function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function buildNameCandidates(rawName: string): string[] {
  const lower = rawName.toLowerCase().trim().replace(/^@/, '');
  const withoutParens = lower
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const withoutVersionTail = withoutParens
    .replace(/([:@/ ]+)?v?\d[\da-z.+-]*(?:\s.*)?$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const splitBySeparator = withoutParens
    .split(/[:/@]/)
    .map((part) => part.trim())
    .filter(Boolean);

  const tokenCandidates = withoutParens
    .split(/[^a-z0-9]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 1 && !GENERIC_NOISE_TOKENS.has(token));

  return [
    ...new Set([
      lower,
      withoutParens,
      withoutVersionTail,
      ...splitBySeparator,
      ...tokenCandidates,
    ]),
  ].filter(Boolean);
}

function resolveDirectIconName(candidate: string): TechnologyIconName | null {
  return SUPPORTED_ICON_BY_NORMALIZED_NAME.get(normalizeKey(candidate)) ?? null;
}

export const resolveIconName = (
  rawName: string | undefined | null,
): TechnologyIconName | null => {
  if (!rawName) return null;

  const normalizedRaw = normalizeKey(rawName.trim());

  if (normalizedRaw === 'bedrock' || normalizedRaw === 'amazonbedrock') {
    return 'bedrock';
  }

  const candidates = buildNameCandidates(rawName);

  for (const candidate of candidates) {
    const directIcon = resolveDirectIconName(candidate);

    if (directIcon) {
      return directIcon;
    }
  }

  for (const candidate of candidates) {
    const matchedPattern = ICON_PATTERNS.find(({ pattern }) =>
      pattern.test(candidate),
    );

    if (matchedPattern) {
      return matchedPattern.icon;
    }
  }

  return null;
};
